import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useTenancies from "features/tenancies";
import HeroSection4 from "components/HeroSection4";
import FactSection1 from "components/FactSection1";
import CarouselSection1 from "components/CarouselSection1";
import FactSection2 from "components/FactSection2";
import FacilitiesSection1 from "components/FacilitiesSection1";
import ImageSection1 from "components/ImageSection1";
import ImageGridSection2 from "components/ImageGridSection2";
import AvailableTenanciesList from "components/AvailableTenanciesList";
import ContactSection from "components/ContactSection";
import Button from "components/Button";
import InterestListButton from "components/InterestListButton";
import imageMapJSON from "./components/imageMapJSON";
import background01 from "assets/properties/labyrinthen/background-maze.png";
import imageProperty01 from "assets/properties/trylleby/trylleby-property-01.jpg";
import imageProperty02 from "assets/properties/trylleby/trylleby-property-02.jpg";
import imageProperty03 from "assets/properties/trylleby/trylleby-property-03.jpg";
import imageProperty10 from "assets/properties/trylleby/trylleby-property-10.jpg";
import imageProperty11 from "assets/properties/trylleby/trylleby-property-11.jpg";
import imageProperty19 from "assets/properties/trylleby/trylleby-property-19.jpg";
import imageProperty21 from "assets/properties/trylleby/trylleby-property-21.jpg";
import imageProperty22 from "assets/properties/trylleby/trylleby-property-22.jpg";
import imageProperty23 from "assets/properties/trylleby/trylleby-property-23.jpg";
import imageProperty24 from "assets/properties/trylleby/trylleby-property-24.jpg";
import imageProperty25 from "assets/properties/trylleby/trylleby-property-25.jpg";
import imageProperty26 from "assets/properties/trylleby/trylleby-property-26.jpg";
import imageProperty27 from "assets/properties/trylleby/trylleby-property-27.jpg";
import backgroundMovie from "assets/properties/trylleby/trylleby.mp4";
import { ReactComponent as StarsIcon } from "assets/icons/stars.svg";
import { ReactComponent as ResizeIcon } from "assets/icons/resize-arrows.svg";
import { ReactComponent as BuildingIcon } from "assets/icons/building.svg";
import { ReactComponent as ParkingIcon } from "assets/icons/parking2.svg";
import { ReactComponent as WashingIcon } from "assets/icons/washer.svg";
import { ReactComponent as GreenAreasIcon1 } from "assets/icons/green-areas.svg";
import { ReactComponent as TerraceIcon } from "assets/icons/terrace.svg";
import { ReactComponent as ShedIcon } from "assets/icons/shed.svg";
import { ReactComponent as PlaygroundIcon } from "assets/icons/playground.svg";
import { ReactComponent as ToiletIcon } from "assets/icons/toilet.svg";
import ImageMapIsometrySection from "components/ImageMapIsometrySection";

const Wrapper = styled.div`
	margin: 0 auto;
	background-color: #fafafa;
`;

const Trylleby = () => {
	const { t } = useTranslation();

	const { isTenanciesLoading, tenanciesData, loadTenanciesByPropertyId } =
		useTenancies();

	const propertyId = "89372d92-6733-4465-abf5-d3e347b64e26";

	useEffect(() => {
		loadTenanciesByPropertyId(propertyId);
	}, [loadTenanciesByPropertyId]);

	const factSectionBoxes = [
		{
			pre: t("properties.trylleby.factSection1.boxes.text1.pre"),
			bold: t("properties.trylleby.factSection1.boxes.text1.bold"),
			after: t("properties.trylleby.factSection1.boxes.text1.after"),
			icon: <StarsIcon />,
		},
		{
			pre: t("properties.trylleby.factSection1.boxes.text2.pre"),
			bold: t("properties.trylleby.factSection1.boxes.text2.bold"),
			after: t("properties.trylleby.factSection1.boxes.text2.after"),
			icon: <BuildingIcon />,
		},
		{
			pre: t("properties.trylleby.factSection1.boxes.text3.pre"),
			bold: t("properties.trylleby.factSection1.boxes.text3.bold"),
			after: t("properties.trylleby.factSection1.boxes.text3.after"),
			icon: <ResizeIcon />,
		},
	];

	const facilities = [
		{
			icon: <ParkingIcon />,
			text: t("properties.trylleby.facilitySection.text5"),
		},
		{
			icon: <TerraceIcon />,
			text: t("properties.trylleby.facilitySection.text1"),
		},
		{
			icon: <WashingIcon />,
			text: t("properties.trylleby.facilitySection.text4"),
		},
		{
			icon: <PlaygroundIcon />,
			text: t("properties.trylleby.facilitySection.text3"),
		},
		{
			icon: <ShedIcon />,
			text: t("properties.trylleby.facilitySection.text2"),
		},
		{
			icon: <GreenAreasIcon1 />,
			text: t("properties.trylleby.facilitySection.text6"),
		},
		{
			icon: <WashingIcon />,
			text: t("properties.trylleby.facilitySection.text7"),
		},
		{
			icon: <ToiletIcon />,
			text: t("properties.trylleby.facilitySection.text8"),
		},
	];

	const sliderImages = [imageProperty03, imageProperty02, imageProperty01, imageProperty10, imageProperty11];

	const imagesGrid = [
		imageProperty21,
		imageProperty22,
		imageProperty23,
		imageProperty25,
		imageProperty26,
		imageProperty27,
	];
	

	const contactSectionRef = useRef(null);
	const scrollToContactSection = () => {
		contactSectionRef.current.scrollIntoView({ behavior: "smooth" });
	};
	const isometrySectionRef = useRef(null);
	const scrollToIsometrySection = () => {
		isometrySectionRef.current.scrollIntoView({ behavior: "smooth" });
	};

	return (
		<Wrapper>
			<HeroSection4
				background={backgroundMovie}
				backgroundType={"video"}
				title={t("properties.trylleby.title")}
				text={t("properties.trylleby.subtitle")}
				primaryButtons={<InterestListButton propertyId={propertyId} />}
				secondaryButtons={
					<Button onClick={scrollToContactSection}>
						{t("properties.general.contactUs")}
					</Button>
				}
			/>
			<FactSection1
				background={background01}
				title={t("properties.trylleby.factSection1.title")}
				textBase={t("properties.trylleby.factSection1.textBase")}
				textAdditional={t(
					"properties.trylleby.factSection1.textAdditional"
				)}
				buttonText={t("properties.trylleby.factSection1.buttonText")}
				buttonOnClick={scrollToIsometrySection}
				boxes={factSectionBoxes}
			/>
			<CarouselSection1
				title={t("properties.trylleby.carouselSection.title")}
				section1title={t(
					"properties.trylleby.carouselSection.section1title"
				)}
				section1text={t(
					"properties.trylleby.carouselSection.section1text"
				)}
				images={sliderImages}
			/>
			<FactSection2
				title1={t("properties.trylleby.factSection2.title1")}
				text1={t("properties.trylleby.factSection2.text1")}
				title2={t("properties.trylleby.factSection2.title2")}
				text2={t("properties.trylleby.factSection2.text2")}
			/>
			<FacilitiesSection1
				title={t("properties.trylleby.facilitySection.title")}
				facilities={facilities}
			/>
			<ImageSection1 image={imageProperty24} />
			<ImageGridSection2 images={imagesGrid} />
			<div ref={isometrySectionRef}>
				<ImageMapIsometrySection 
					title={t("properties.trylleby.ImageMapSection.title")}
					text={t("properties.trylleby.ImageMapSection.text")}
					imageWidth={2560}
					imageMapJSON={imageMapJSON}
					image={imageProperty19}
					tenancies={tenanciesData}
					loading={isTenanciesLoading}
				/>
			</div>
			{tenanciesData?.length > 0 && (
				<AvailableTenanciesList
					loading={isTenanciesLoading}
					tenancies={tenanciesData}
				/>
			)}
			<div ref={contactSectionRef}>
				<ContactSection backgroundColor='#fafafa' />
			</div>
		</Wrapper>
	);
};

export default Trylleby;
